import { NgIf } from '@angular/common';
import { ChangeDetectorRef, Component, HostBinding, OnInit } from '@angular/core';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';

import { Fixture, LiveType } from '@cds/betting-offer';
import { SplitFixture } from '@cds/betting-offer/domain-specific';
import { EnhancedFixtureViewGroupingConfiguration, EventModel } from '@frontend/sports/betting-offer/feature/model';
import { OfferGroupingService } from '@frontend/sports/betting-offer/feature/offer-grouping';
import { CDNCachingConfig, Sitecore } from '@frontend/sports/common/client-config-data-access';
import { NumberDictionary, StringDictionary, isDefined } from '@frontend/sports/common/core/utils/extended-types';
import { RedirectHelperService } from '@frontend/sports/navigation/core/feature/redirect';
import { INativePrice, NativePrice } from '@frontend/sports/odds/feature/native-price';
import { PodsViewModel, TopEventMarketFixtureSource, TopEventPodTypes } from '@frontend/sports/types/components/top-events';
import { WidgetContext, WidgetType } from '@frontend/sports/types/components/widget';
import { TopEventsPayload } from '@frontend/sports/types/components/widget/types';
import { cloneDeep, entries, groupBy, includes, partition, uniq } from 'lodash-es';
import { BehaviorSubject, EMPTY, Observable, filter, map, noop, switchMap } from 'rxjs';

import { ShowInEpcotDirective } from '../../common/epcot-config.directive';
import { WidgetCommonModule } from '../../widget/common/widget-common.module';
import { WidgetContextService } from '../../widget/common/widget-context.service';
import { WidgetComponent } from '../../widget/core/widget-registry.service';
import { TopEventsMappingService } from '../top-events-mapping.service';
import { TopEventsPushService } from '../top-events-push.service';
import { TopEventsTrackingService } from '../top-events-tracking.service';
import { LinkConfig, PodType, TopEventsModuleData, TopEventsPodData, TopEventsSourceFixture } from '../top-events.model';
import { TopEventsModule } from '../top-events.module';

@Component({
    imports: [NgIf, TopEventsModule, WidgetCommonModule, ShowInEpcotDirective],
    selector: 'ms-top-events-widget',
    templateUrl: './top-events-widget.component.html',
    providers: [TopEventsTrackingService],
})
export class TopEventsWidgetComponent extends WidgetComponent<TopEventsPayload> implements OnInit {
    @HostBinding('class') className = 'top-events-widget grid-wrapper';

    module: TopEventsModuleData;
    headerLinkConfig: LinkConfig;

    private readonly podsTypesWithDetailedGrouping = [PodType.Single, PodType.PropsMultiPod, PodType.BetBuilder];
    private data$ = new BehaviorSubject<TopEventsPayload | undefined>(undefined);
    private shouldIncludePayload = false;

    constructor(
        public sitecore: Sitecore,
        private offerGroupingService: OfferGroupingService,
        private topEventsTrackingService: TopEventsTrackingService,
        private topEventsMappingService: TopEventsMappingService,
        private topEventsPushService: TopEventsPushService,
        private changeDetectionRef: ChangeDetectorRef,
        private redirectService: RedirectHelperService,
        private cdnCachingConfig: CDNCachingConfig,
        private contextService: WidgetContextService,
    ) {
        super();
        this.shouldIncludePayload = this.cdnCachingConfig.widgets.includes(WidgetType.TopEvents);
    }

    ngOnInit(): void {
        this.data$
            .pipe(
                filter(isDefined),
                map((data) => {
                    const podsList = Object.values(data.pods ?? {}).filter((p) => p.fixtures?.length || p.betBuilderFixtures?.length);

                    return { podsList, title: data.title, headerNavLink: data.headerNavLink };
                }),
                switchMap((data) => {
                    if (!data.podsList.length) {
                        this.hasData = false;
                        this.logEmptyData();
                        this.setWidgetLoaded();
                        return EMPTY;
                    }

                    return this.offerGroupingService
                        .getFixtureGroupingForSports(this.podSportIdsWithDetailedGrouping(data.podsList), 'any')
                        .pipe(map((groupings) => ({ data, groupings })));
                }),
                takeUntilDestroyed(this.destroyRef),
            )
            .subscribe(({ data, groupings }) => {
                this.hasData = !!data.podsList.length;
                const podData = this.toTopEventsPodData(data.podsList, groupings ?? []).filter((pod) =>
                    pod.events.some((event) => event.optionGroups.length),
                );

                this.topEventsPushService.resubscribe(podData);

                this.module = {
                    title: data.title,
                    podData,
                    href: data.headerNavLink?.href,
                };

                this.headerLinkConfig = {
                    link: this.module.href,
                    disableLinkTextAndClick: this.getChevronVisibility(),
                };

                this.module.podData = this.filterPods(this.module.podData);

                this.setWidgetLoaded();
                this.trackWidgetLoaded(this.module.podData);

                this.changeDetectionRef.detectChanges();
            });

        this.topEventsPushService.updatedPods$.pipe(takeUntilDestroyed(this.destroyRef)).subscribe(this.pushReceived.bind(this));
    }

    private filterPods(pods: TopEventsPodData[]): TopEventsPodData[] {
        if (pods) {
            pods.forEach((pod) => {
                const marketParameters = pod.marketParameters;
                if (marketParameters?.length) {
                    pod.events.forEach((event) => {
                        const updatedEvent = this.topEventsMappingService.filterEventWithTemplates(event, pod);
                        if (updatedEvent) {
                            const idx = pod.events.findIndex((e) => e.id === updatedEvent.id);
                            if (idx !== -1) {
                                pod.events[idx] = updatedEvent;
                            }
                        }
                    });
                }
            });

            pods = pods.filter((p) => {
                return p.marketParameters?.length ? p.events.every((event) => event.optionGroups.length !== 0) : true;
            });

            return pods;
        }

        return [];
    }

    private pushReceived(updatedPods: StringDictionary<EventModel[]>): void {
        entries(updatedPods).forEach((oe) => {
            const pod = this.module?.podData?.find((p) => p.podId === oe[0]);

            if (pod) {
                oe[1].forEach((event) => {
                    // update event if found with a filtered new event to not add extra options/markets
                    const idx = pod.events.findIndex((e) => e.id === event.id);

                    if (idx !== -1) {
                        pod.events[idx] = pod.marketParameters?.length
                            ? this.topEventsMappingService.filterEventWithTemplates(event, pod)
                            : this.topEventsMappingService.filterEvent(event, pod.sourceFixtures[event.id]);
                    }
                });

                // pod itself will map to its VM and call detectchanges
                pod.updateHandler();
            }
        });
    }

    // triggered 2 times on page load because of lazyWidgets, meaning
    // it will subscribe -> unsubsribe -> subscribe again on page load
    protected override onData(data: TopEventsPayload, isWidgetRefresh: boolean): void {
        if (!data?.pods) {
            this.getData(isWidgetRefresh).subscribe((podData) => this.data$.next(podData));
        } else {
            this.data$.next(data);
        }
    }

    override getContext(): Partial<WidgetContext> {
        const context = super.getContext();

        return !this.shouldIncludePayload ? context : { ...context, shouldIncludePayload: this.shouldIncludePayload };
    }

    private getData(isWidgetRefresh: boolean): Observable<TopEventsPayload> {
        return this.contextService.getWidget<TopEventsPayload>(this.getContext(), !isWidgetRefresh).pipe(
            filter(({ payload }) => payload !== undefined),
            map(({ payload }) => payload!),
            takeUntilDestroyed(this.destroyRef),
        );
    }

    get showBetBuilderBadge(): boolean {
        return !!this.module?.podData.every((p) => p.podType === PodType.BetBuilder);
    }

    private podSportIdsWithDetailedGrouping(pods: PodsViewModel[]): number[] {
        return uniq(
            pods
                .filter((p) => includes(this.podsTypesWithDetailedGrouping, PodType[p.podType]))
                .flatMap((p) => p.fixtures?.map((f) => f.sport.id) ?? []),
        );
    }

    private toTopEventsPodData(pods: PodsViewModel[], groupings: EnhancedFixtureViewGroupingConfiguration[]): TopEventsPodData[] {
        return pods.map((pod, i) => {
            const podSportIds = this.podSportIdsWithDetailedGrouping([pod]);

            const [mainFixtures, splitFixtures] =
                pod.selectMarketFixtureSource === TopEventMarketFixtureSource.Main
                    ? [pod.fixtures, []]
                    : partition(pod.fixtures, (ff) => ff.liveType === LiveType.MainMarkets);

            const groupSplitFixtures = groupBy(splitFixtures, (sf) => sf.groupId);
            const type = PodType[pod.podType];
            const isDetailed = includes(this.podsTypesWithDetailedGrouping, type);

            const createdEvents =
                mainFixtures
                    ?.map((f, idx) => {
                        const groupFixtures = f.groupId ? (groupSplitFixtures[f.groupId] ?? []) : []; //can be undefined when there are no splitFixtures
                        const splitFixturesGroup = groupFixtures.map((fixture) => {
                            return { ...fixture, playerStats: fixture?.playerStats[0] } as SplitFixture;
                        });

                        return this.topEventsMappingService.createFixture(
                            isDetailed,
                            cloneDeep(f),
                            splitFixturesGroup,
                            groupings.find((g) => g.sportId === f.sport.id),
                            type === PodType.BetBuilder && pod.betBuilderFixtures ? pod.betBuilderFixtures[idx] : undefined,
                        );
                    })
                    .filter(isDefined) ?? [];

            const currentExpandState = this.module?.podData.find((p) => p.podId === pod.podInfo.id)?.isExpanded ?? pod.isExpanded;

            return {
                podId: pod.podInfo.id,
                title: pod.title,
                index: i,
                isExpanded: currentExpandState !== pod.isExpanded ? currentExpandState : pod.isExpanded,
                isLogoVisible: pod.isLogoVisible,
                logoOverride: pod.logoOverride || '',
                isPromotionTag: pod.isPromotionTag,
                showAllWagersLink: pod.showAllWagersLink,
                metaData: pod.metaData,
                podType: type,
                events: createdEvents,
                fixtures: pod.fixtures ?? [],
                splitFixtures: splitFixtures,
                trackingInfo: this.getTrackingInfo(pod.fixtures ?? []),
                marketsBeforeShowMore: pod.marketsBeforeShowMore,
                eventsBeforeShowMore: pod.eventsBeforeShowMore,
                optionsBeforeShowMore: pod.optionsBeforeShowMore,
                mobileColumns: pod.mobileColumns,
                link: pod.link,
                linkDefaultText: pod.linkDefaultText,
                enableTitleLink: pod.enableTitleLink,
                selectionTextOverride: pod.selectionTextOverride,
                hideBetBuilderButton: pod.hideBetBuilderButton,
                hideMetadataFallback: pod.hideMetadataFallback,
                hidePodHeader: pod.hidePodHeader,
                hideBottomLink: pod.hideBottomLink,
                detailedGroupings: groupings?.filter((g) => includes(podSportIds, g.sportId)) ?? [],
                isDetailed,
                updateHandler: noop,
                sourceFixtures: this.toSourceFixtures(
                    createdEvents,
                    pod.podType === TopEventPodTypes.HandpickedOptionsPod ? pod.fixtures : undefined,
                ),
                hideMarketHeader: pod.hideMarketHeader,
                blackListMarketGroupIds: pod.blackListMarketGroupIds,
                marketParameters: pod.marketParameters,
                betBuilderOptionIds: pod.betBuilderOptionIds,
                previousOdds: this.previousOddsMapper(pod.previousOdds),
                marketFixtureSource: pod.selectMarketFixtureSource,
                showBetBuilderBadgeOnHeader: !!pod.showSGPBadge,
            };
        });
    }

    private previousOddsMapper(previousOdds: NumberDictionary<INativePrice> | undefined): NumberDictionary<NativePrice> | undefined {
        if (!previousOdds) {
            return;
        }

        return Object.fromEntries(Object.entries(previousOdds).map(([key, value]) => [key, NativePrice.fromNativePrice(value)]));
    }

    private toSourceFixtures(events: EventModel[], fixtures: Fixture[] = []): StringDictionary<TopEventsSourceFixture> {
        const result: StringDictionary<TopEventsSourceFixture> = {};

        events.forEach(
            (e) =>
                (result[e.id] = {
                    id: e.id,
                    optionGroupIds: new Set(
                        e.optionGroups.flatMap((og) => [og.originalId ?? og.id, ...(og.subGroups?.flatMap((sg) => sg.originalId ?? sg.id) ?? [])]),
                    ),
                    optionIds: new Set(
                        this.getOptionIds(
                            e,
                            fixtures.find((f) => f.id === e.id),
                        ),
                    ),
                } as TopEventsSourceFixture),
        );

        return result;
    }

    private getOptionIds(event: EventModel, fixture: Fixture | undefined): number[] {
        if (fixture) {
            return (fixture.games?.flatMap((g) => g.results.map((r) => r.id)) ?? []).concat(
                fixture.optionMarkets?.flatMap((om) => om.options.map((o) => o.id)) ?? [],
            );
        }

        return event.optionGroups.flatMap((og) => og.options.map((o) => o.id));
    }

    private getTrackingInfo(fixtures: Fixture[]): StringDictionary<string> {
        const trackingData: StringDictionary<string> = {};
        if (!fixtures.length || fixtures.some((f) => f === undefined)) {
            return trackingData;
        }
        const competitionGroups = groupBy(fixtures, (f) => f.competition.id);

        entries(competitionGroups).forEach((group) => {
            trackingData[group[0]] = uniq([
                ...group[1].flatMap((fixture) => fixture.optionMarkets.map((om) => om.id)),
                ...group[1].flatMap((fixture) => fixture.games.map((g) => g.id)),
            ]).join(',');
        });

        return trackingData;
    }

    private trackWidgetLoaded(podData: TopEventsPodData[]): void {
        this.topEventsTrackingService.trackWidgetLoad(podData);
    }

    private getChevronVisibility(): boolean {
        return Boolean(this.module.href) && Boolean(this.module.title);
    }

    handleHeaderNavigation(): void {
        if (this.module.href) {
            this.redirectService.goToPage(this.module.href);
        }
    }
}
